/**
 * 匿名玩家 session（C02 §3、C03 §2、**C03 §5.1**）。
 *
 * ★ 儲存規則（C03 §5 + §5.1，不可違反）：
 *   token 存 **記憶體 + sessionStorage**，**絕不放 localStorage**。
 *   **這一條沒有變，而且對 token 永遠成立**（§5.1 明文重申）。
 *
 * ## 變的是「為什麼可以只放 sessionStorage」—— 舊的理由已經作廢
 *
 * 這裡原本寫的理由是「sessionStorage 只活在這個分頁，關掉就沒了」，
 * 並把那個副作用當成優點。C03 §5.1（v1.3）推翻了那個產品前提：
 * **匿名身分必須在同一個瀏覽器裡持久** —— 玩家關掉分頁再回來要還是同一個人，
 * 之後註冊時那些對局紀錄要能一起帶走。
 *
 * 持久性現在由**後端發的 httpOnly + Secure + SameSite=Lax cookie** 提供：
 *
 *     httpOnly cookie（JS 讀不到）  ──POST /auth/guest 換發──▶  短命 bearer token（記憶體）
 *
 * 所以這個 store 已經**不是「你是誰」的來源**，只是這個分頁的 token 快取 ——
 * 存在的目的縮小成「F5 之後不必再換發一次」。真正的身分在 cookie 裡，
 * 而 cookie 這支程式碼**碰不到、也不該碰**（`document.cookie` 讀不到 httpOnly）。
 *
 * ## 為什麼「持久」與「不放 localStorage」不衝突
 *
 * §5 要防的從來不是「長命憑證」本身，而是**XSS 把長命憑證偷走後離線長期利用**。
 * `localStorage.getItem()` 一行就能把 token 帶走；httpOnly cookie **沒有任何 JS API**
 * 讀得到。而且 cookie 裡放的是一個**只能用來換發**的憑證，不是 bearer token 本身 ——
 * 就算換到手，它也只是換到一張 24h 的 token，不是一把萬能鑰匙。
 *
 * ⚠️ 殘餘風險（C03 §5.1 誠實記著，這裡照抄以免哪天有人以為 httpOnly 是萬靈丹）：
 * httpOnly 擋得住**竊取**，擋不住**濫用** —— XSS 仍然可以在受害者的瀏覽器裡代他發請求
 * （cookie 會自動附上）。差別是攻擊者**帶不走**憑證：關掉頁面攻擊就結束。
 *
 * guest token 本身壽命 24h（C03 §2），我們額外在到期前 60s 就視為失效並重新換發。
 */

import type { GuestTokenResponse } from '../api/types';

export interface GuestSession {
  kind?: 'guest' | 'player';
  token: string;
  subjectId: string;
  displayName: string;
  expiresAt: string;
}

const EXPIRY_SKEW_MS = 60_000;

export class GuestSessionStore {
  private readonly key: string;
  private readonly storage: Storage | null;
  /**
   * 記憶體是第一手來源；sessionStorage 只是為了 F5 之後還在。
   * **不是**跨分頁／跨開關瀏覽器的身分來源 —— 那件事由 httpOnly cookie 負責（C03 §5.1），
   * 這裡看不到它，所以這個 store 空的時候不代表「這是個新玩家」，
   * 只代表「這個分頁手上沒有 token，去換一張」。
   */
  private memory: GuestSession | null = null;
  private listeners = new Set<(s: GuestSession | null) => void>();

  constructor(storagePrefix: string, storage: Storage | null = safeSessionStorage()) {
    this.key = `${storagePrefix}guest`;
    this.storage = storage;
    this.memory = this.readFromStorage();
  }

  private readFromStorage(): GuestSession | null {
    if (!this.storage) return null;
    try {
      const raw = this.storage.getItem(this.key);
      if (!raw) return null;
      const parsed = JSON.parse(raw) as Partial<GuestSession>;
      if (!parsed.token || !parsed.subjectId || !parsed.expiresAt) return null;
      const s: GuestSession = {
        kind: parsed.kind,
        token: parsed.token,
        subjectId: parsed.subjectId,
        displayName: parsed.displayName ?? '',
        expiresAt: parsed.expiresAt,
      };
      return isUsable(s) ? s : null;
    } catch {
      return null;
    }
  }

  get(): GuestSession | null {
    if (this.memory && !isUsable(this.memory)) this.clear();
    return this.memory;
  }

  getToken(): string | null {
    return this.get()?.token ?? null;
  }

  set(resp: GuestTokenResponse, kind: 'guest' | 'player' = 'guest'): GuestSession {
    const session: GuestSession = {
      kind,
      token: resp.guest_token,
      subjectId: resp.subject_id,
      displayName: resp.display_name,
      expiresAt: resp.expires_at,
    };
    this.memory = session;
    try {
      this.storage?.setItem(this.key, JSON.stringify(session));
    } catch {
      /* storage 滿了或被停用：記憶體那份還在，功能不受影響 */
    }
    this.emit();
    return session;
  }

  clear(): void {
    this.memory = null;
    try {
      this.storage?.removeItem(this.key);
    } catch {
      /* ignore */
    }
    this.emit();
  }

  subscribe(fn: (s: GuestSession | null) => void): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  private emit(): void {
    for (const fn of this.listeners) fn(this.memory);
  }
}

export function isUsable(s: GuestSession): boolean {
  const exp = Date.parse(s.expiresAt);
  if (Number.isNaN(exp)) return false;
  return exp - EXPIRY_SKEW_MS > Date.now();
}

function safeSessionStorage(): Storage | null {
  try {
    if (typeof globalThis.sessionStorage === 'undefined') return null;
    // Safari 無痕模式會讓 setItem 直接丟例外，先探一次。
    const probe = '__elabgaming_probe__';
    globalThis.sessionStorage.setItem(probe, '1');
    globalThis.sessionStorage.removeItem(probe);
    return globalThis.sessionStorage;
  } catch {
    return null;
  }
}
